import React from 'react';
import { Box, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { TierName } from '../../types/ranked';
import { tierColor } from './tierColors';

interface TierBadgeProps {
  tier: TierName;
  label: string;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

const DIMENSIONS = {
  small: { width: 32, height: 36, icon: 14, label: '0.75rem' },
  medium: { width: 48, height: 54, icon: 20, label: '1rem' },
  large: { width: 62, height: 70, icon: 26, label: '1.5rem' }
};

/**
 * Hexagonal rank insignia.
 *
 * The tier name is what students see day to day; the raw rating stays opt-in.
 * A hexagon rather than a circle so it reads as an earned insignia rather than
 * another avatar — the app already uses circles for people.
 */
const TierBadge: React.FC<TierBadgeProps> = ({
  tier,
  label,
  size = 'medium',
  showLabel = true
}) => {
  const color = tierColor(tier);
  const dims = DIMENSIONS[size];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        aria-hidden
        sx={{
          width: dims.width,
          height: dims.height,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: color,
          color: '#fff',
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          transition: 'background-color 0.35s ease'
        }}
      >
        <StarIcon sx={{ fontSize: dims.icon }} />
      </Box>

      {showLabel && (
        <Typography
          fontWeight={700}
          sx={{ color, fontSize: dims.label, lineHeight: 1.15, letterSpacing: '-0.02em' }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
};

export default TierBadge;
